import { useRef } from "react";
import { BookingButton } from "@/components/BookingButton";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { lineReveal, IMAGES } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";

const HERO_LINES = [
  { t: "Structurer aujourd'hui", gold: false },
  { t: "les projets qui façonneront", gold: false },
  { t: "durablement les territoires", gold: false },
  { t: "de demain.", gold: true },
];

const WHY = [
  { t: "Une vision stratégique", d: "Nous analysons les projets dans leur globalité afin d'identifier les facteurs qui conditionnent leur réussite, d'éclairer les décisions et d'anticiper les risques avant qu'ils ne deviennent des contraintes." },
  { t: "Une expertise multidisciplinaire", d: "Les projets complexes exigent une lecture transversale. Notre approche intègre les dimensions stratégiques, institutionnelles, organisationnelles, techniques et humaines pour des solutions cohérentes et adaptées à chaque contexte." },
  { t: "Une expérience opérationnelle", d: "Notre expertise est nourrie par une expérience de terrain acquise sur des projets industriels et énergétiques internationaux, gage de recommandations pragmatiques, applicables et en phase avec les contraintes d'exécution." },
  { t: "Une indépendance au service de la décision", d: "Notre indépendance garantit des recommandations fondées sur les faits, l'analyse et les objectifs de long terme, guidées par le seul intérêt du projet et la qualité de sa gouvernance." },
];

const EXPERTISES = [
  { n: "01", t: "Stratégie & Structuration", d: "Donner aux projets les fondations de leur réussite : orientations, structuration et préparation des décisions déterminantes." },
  { n: "02", t: "Gouvernance & Performance", d: "Créer les conditions d'une décision efficace et durable, à travers des dispositifs de gouvernance et des cadres d'évaluation adaptés." },
  { n: "03", t: "Transition énergétique", d: "Accompagner les mutations énergétiques : décarbonation, nouvelles filières et infrastructures de demain." },
  { n: "04", t: "Transformation & Accompagnement", d: "Conduire le changement avec méthode et proximité, et renforcer durablement les capacités des organisations." },
];

export default function Home() {
  useSEO({
    title: "Structuration de projets, gouvernance & transition énergétique",
    description:
      "HERUS Axiom, maison d'expertise stratégique : structuration de projets, gouvernance, transition énergétique et transformation des organisations, en Afrique et en Europe.",
    keywords:
      "structuration de projets, gouvernance, partenariats public-privé, PPP, infrastructures, transition énergétique, conseil stratégique, transformation des organisations, développement durable, Afrique, institutions publiques, investisseurs",
    path: "/",
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden grain">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={IMAGES.heroBridge} alt="Pont à haubans — projet structurant" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />

        <div className="relative z-10 min-h-[100svh] mx-auto max-w-[1600px] px-6 lg:px-12 flex flex-col justify-end pt-32 pb-20 lg:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}>
            <Overline>Maison d'expertise stratégique · Afrique & Europe</Overline>
          </motion.div>

          <h1 className="mt-8 font-serif-display font-light leading-[0.98] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-7xl max-w-5xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block" variants={lineReveal} custom={i} initial="hidden" animate="visible">
                  {line.gold ? <span className="italic text-[#C5A059]">{line.t}</span> : line.t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
          >
            <p className="max-w-2xl text-[#F9F6F0]/75 text-base lg:text-lg font-light leading-relaxed">
              Les infrastructures ne créent de la valeur que lorsqu'elles reposent sur une vision claire,
              une gouvernance solide et une exécution maîtrisée. HERUS Axiom accompagne les acteurs
              publics, les investisseurs et les organisations dans la structuration, la gouvernance et la
              transformation de projets complexes, afin de renforcer leur performance, leur résilience et
              leur impact.
            </p>
            <div className="flex flex-col gap-4 shrink-0">
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/a-propos"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 text-sm uppercase tracking-[0.2em] bg-[#C5A059] text-[#0A0A0A] px-8 py-4 hover:bg-[#F9F6F0] transition-colors duration-500"
              >
                Découvrir HERUS
                <ArrowRight size={18} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center justify-center gap-3 text-sm uppercase tracking-[0.2em] text-[#F9F6F0] border border-white/25 px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-500"
              >
                Échanger sur votre projet
                <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
              <BookingButton
                testId="hero-booking-btn"
                iconSize={18}
                className="inline-flex items-center justify-center gap-3 text-sm uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/60 px-8 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#F9F6F0] border-y border-[#0A0A0A]/10 py-4">
        <Marquee speed={34} gradient={false}>
          {["Éclairer les décisions", "Structurer les projets", "Renforcer la gouvernance", "Accompagner la transformation", "Créer une valeur durable"].map((w, i) => (
            <span key={i} className="font-serif-display italic text-xl lg:text-3xl text-[#0A0A0A]/90 mx-8 flex items-center gap-8">
              {w}
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* SECTION 2 — RAISON D'ÊTRE */}
      <section className="bg-[#0A0A0A] py-24 lg:py-40">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><Overline>Notre conviction</Overline></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal i={1}>
              <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] text-[#F9F6F0]">
                Les projets qui créent une <span className="text-[#C5A059]">valeur durable</span> ne
                doivent rien au hasard. Ils sont le fruit d'une vision stratégique, d'une
                structuration rigoureuse, d'une gouvernance efficace et d'une <span className="italic">exécution maîtrisée</span>.
              </p>
            </Reveal>
            <div className="mt-12 space-y-6 max-w-3xl border-t border-white/10 pt-10">
              <Reveal i={1}>
                <p className="text-[#F9F6F0]/65 font-light leading-relaxed">
                  Les projets d'infrastructures et de transition énergétique mobilisent une diversité
                  d'acteurs, d'intérêts et de contraintes. Leur réussite dépend autant de leur
                  préparation que des ressources qui leur seront consacrées.
                </p>
              </Reveal>
              <Reveal i={2}>
                <p className="text-[#F9F6F0]/65 font-light leading-relaxed">
                  HERUS intervient en amont des décisions les plus structurantes afin d'aider les
                  organisations à construire des projets cohérents, robustes et durables, capables de
                  répondre aux exigences des investisseurs, des partenaires institutionnels et des
                  territoires qu'ils servent.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — POURQUOI HERUS */}
      <section className="bg-[#F9F6F0] py-24 lg:py-32">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <Reveal>
            <Overline>Au-delà du conseil</Overline>
            <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light italic text-[#0A0A0A] leading-tight max-w-4xl">
              L'exigence au service de la décision.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
            {WHY.map((w, i) => (
              <Reveal key={i} i={i} className="group bg-white rounded-[20px] border border-[#0A0A0A]/[0.08] shadow-[0_2px_20px_rgba(10,10,10,0.04)] p-10 lg:p-14 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_18px_50px_rgba(10,10,10,0.10)] hover:border-[#C5A059]/50">
                <span className="font-serif-display text-5xl text-[#C5A059] leading-none block">0{i + 1}</span>
                <h3 className="mt-7 font-serif-display text-2xl lg:text-3xl text-[#0A0A0A]">{w.t}</h3>
                <p className="mt-5 text-[#0A0A0A]/60 font-light leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — NOS EXPERTISES */}
      <section className="bg-[#0A0A0A] py-24 lg:py-40">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <Reveal>
              <Overline>Nos domaines d'expertise</Overline>
              <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#F9F6F0] leading-[0.95]">
                Là où la valeur
                <br /> <span className="italic text-[#C5A059]">se construit.</span>
              </h2>
            </Reveal>
            <Reveal i={1}>
              <Link to="/expertises" data-testid="domains-link" className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#F9F6F0] border-b border-[#C5A059] pb-2">
                Toutes les expertises
                <ArrowUpRight size={18} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 border-t border-l border-white/10">
            {EXPERTISES.map((d, i) => (
              <Reveal key={d.n} i={i} className="group border-b border-r border-white/10 p-10 lg:p-14 hover:bg-[#F9F6F0] transition-colors duration-500">
                <span className="font-serif-display text-5xl text-[#C5A059]">{d.n}</span>
                <h3 className="mt-8 font-serif-display text-3xl text-[#F9F6F0] group-hover:text-[#0A0A0A] transition-colors duration-500">{d.t}</h3>
                <p className="mt-4 text-[#F9F6F0]/55 group-hover:text-[#0A0A0A]/65 font-light leading-relaxed transition-colors duration-500">{d.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — NOTRE APPROCHE */}
      <section className="relative bg-[#0A0A0A] overflow-hidden border-t border-white/10">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden grain bg-[#0A0A0A]">
            <img src={IMAGES.networkNight} alt="Échangeur autoroutier de nuit — interconnexions et flux" className="absolute inset-0 h-full w-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/70" />
          </div>
          <div className="flex items-center px-6 lg:px-20 py-24 lg:py-40">
            <div>
              <Overline>Notre approche</Overline>
              <Reveal i={1}>
                <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#F9F6F0] leading-tight">
                  L'analyse systémique
                  <br /> <span className="italic text-[#C5A059]">au cœur de l'action.</span>
                </h2>
              </Reveal>
              <Reveal i={2}>
                <p className="mt-8 text-[#F9F6F0]/70 font-light leading-relaxed max-w-lg">
                  Chaque mission repose sur une approche globale : compréhension des interactions entre
                  parties prenantes et alignement des décisions avec les objectifs de long terme. Nous
                  appliquons la méthodologie SIIGMAA+G®, un cadre propriétaire qui structure les
                  décisions, sécurise les étapes clés et renforce la gouvernance des projets.
                </p>
              </Reveal>
              <Reveal i={3}>
                <Link to="/approche-gouvernance-projets" data-testid="methodology-link" className="group inline-flex items-center gap-3 mt-10 text-sm uppercase tracking-[0.2em] text-[#F9F6F0] border border-white/25 px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-500">
                  Découvrir notre approche
                  <ArrowRight size={18} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
